<template>
  <b-row>
    <b-col class="my-1" md="2" sm="6">
      <b-form-group class="mb-0">
        <label class="d-inline-block text-sm-left mr-50">Par page</label>
        <b-form-select
            id="perPageSelect"
            v-model="currPerPage"
            :options="pageOptions"
            class="w-50"
            size="sm"
        />
      </b-form-group>
    </b-col>
    <b-col md="6"/>
    <b-col class="my-1" md="4">
      <b-form-group>
        <b-input-group size="sm">
          <b-form-input
              id="filterInput"
              v-model="toFilter"
              placeholder="Rechercher"
              type="search"
          />
          <b-input-group-append>
            <b-button :disabled="!toFilter" @click="toFilter = ''">
              Effacer
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </b-form-group>
    </b-col>

    <b-col cols="12">
      <b-table
          ref="refListTable"
          :current-page="page"
          :fields="fields"
          :filter="toFilter"
          :items="items"
          :per-page="currPerPage"
          :sort-by.sync="toSortBy"
          :sort-desc.sync="toSortDesc"
          empty-text="Aucun enregistrements correspondants trouvés"
          hover
          responsive
          show-empty
          striped
      >
        <template #cell(id)="data">
          {{ data.index + 1 }}
        </template>
        <template #cell(debit)="data">
          {{ amountToCurrency(data.item.debit, 'fr-FR', null) }}
        </template>
        <template #cell(credit)="data">
          {{ amountToCurrency(data.item.credit, 'fr-FR', null) }}
        </template>
        <template #cell(balance)="data">
          {{ amountToCurrency(data.item.balance, 'fr-FR', null) }}
        </template>
        <template #cell(amount)="data">
          {{ amountToCurrency(data.item.amount, 'fr-FR', null) }}
        </template>
        <template #cell(approved_amount)="data">
          {{ amountToCurrency(data.item.approved_amount, 'fr-FR', null) }}
        </template>
        <template #cell(total_paid)="data">
          {{ amountToCurrency(data.item.total_paid, 'fr-FR', null) }}
        </template>
        <!-- Column: Project code -->
        <template #cell(project)="data">
          <div v-if="data.item.opportunity">
            <b-link
                v-if="data.item.opportunity.is_won"
                :to="{
                name: 'projects-view',
                params: { id: data.item.opportunity.uid },
              }"
                class="font-weight-bold"
            >
              #{{ opportunityCode(data.item) }}
            </b-link>
            <b-link
                v-else
                :to="{
                name: 'apps-bids-view',
                params: { id: data.item.opportunity.uid },
              }"
                class="font-weight-bold"
            >
              #{{ opportunityCode(data.item) }}
            </b-link>
          </div>
        </template>
        <!-- Column: Project date -->
        <template #cell(created_at)="data">
          {{ formatDate(data.item.created_at) }}
        </template>
        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <div class="text-nowrap">
            <expense-handler
                v-if="data.item.status === 'pending' && data.item.opportunity && $can('edit_request', 'Caisse Générale')"
                :btnFormat="false"
                :index="data.index"
                :opportunityId="data.item.opportunity ? data.item.opportunity.uid : ''"
                :requestData="data.item"
                class="d-inline"
                @expenseUpdated="expenseHandled"
            />
            <b-button
                v-if="$can('approve_request', 'Caisse Générale')"
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                class="btn-icon rounded-circle"
                variant="flat-success"
                @click="approveRequest(data.item)"
            >
              <feather-icon
                  :id="`expense-row-${data.item.id}-approve-icon`"
                  :icon="
                  data.item.status === 'pending' ? 'CheckIcon' : 'UploadIcon'
                "
                  size="16"
              />
            </b-button>
            <b-tooltip
                :target="`expense-row-${data.item.id}-approve-icon`"
                :title="
                data.item.status === 'pending'
                  ? 'Approuver Requête'
                  : 'Effectuer Paiement'
              "
                class="cursor-pointer"
            />

            <b-button
                v-if="(data.item.status === 'pending' || data.item.status === 'approved') && $can('revoke_request', 'Caisse Générale')"
                class="btn-icon rounded-circle"
                variant="flat-danger"
                @click="rejectRequest(data.item)"
            >
              <feather-icon
                  :id="`expense-row-${data.item.id}-reject-icon`"
                  icon="XIcon"
                  size="16"
              />
            </b-button>
            <b-tooltip
                v-if="data.item.status === 'pending' || data.item.status === 'approved'"
                :target="`expense-row-${data.item.id}-reject-icon`"
                title="Rejeter Requête"
            />
          </div>
        </template>
      </b-table>
    </b-col>

    <b-col cols="12">
      <b-pagination
          :id="`pagination-${concernedList}`"
          v-model="page"
          :per-page="currPerPage"
          :total-rows="total"
          align="center"
          class="mb-0 mt-1 mt-sm-0"
          first-number
          last-number
          next-class="next-item"
          prev-class="prev-item"
      >
        <template #prev-text>
          <feather-icon icon="ChevronLeftIcon" size="18"/>
        </template>
        <template #next-text>
          <feather-icon icon="ChevronRightIcon" size="18"/>
        </template
        >
      </b-pagination>
    </b-col>
  </b-row>
</template>

<script>
import expensesMixin from "@/mixins/expenses"
import {
  BButton,
  BCol,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BInputGroup,
  BInputGroupAppend,
  BLink,
  BPagination,
  BRow,
  BTable,
  BTooltip,
} from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
import ExpenseHandler from "@/components/expenses/ExpenseHandler.vue";
import {amountToCurrency} from "@/utils/formatAmount";
import {formatDate} from "@core/utils/filter";

export default {
  props: {
    concernedList: String,
    fields: {
      type: Array,
      default: () => [],
    },
    perPage: {
      type: Number,
      required: true,
    },
    pageOptions: {
      type: Array,
      default: () => [],
    },
    currentPage: {
      type: Number,
      required: true,
    },
    sortBy: {
      type: String,
      required: true,
    },
    sortDesc: {
      type: Boolean,
      required: true,
    },
    sortDirection: {
      type: String,
      required: true,
    },
    filter: {
      type: String,
      required: true,
    },
    filterOn: {
      type: Array,
      default: () => [],
    },
    items: {
      required: true,
    },
    total: {
      type: Number,
      required: true,
    },
  },
  components: {
    BRow,
    BCol,
    BLink,
    BTooltip,
    BTable,
    BFormGroup,
    BFormSelect,
    BPagination,
    BInputGroup,
    BFormInput,
    BInputGroupAppend,
    BButton,

    ExpenseHandler,
  },
  directives: {
    Ripple,
  },
  mixins: [expensesMixin],
  computed: {
    currPerPage: {
      get() {
        return this.perPage;
      },
      set(value) {
        this.$emit("update:perPage", value);
      },
    },
    page: {
      get() {
        return this.currentPage;
      },
      set(value) {
        this.$emit("update:currentPage", value);
      },
    },
    toSortBy: {
      get() {
        return this.sortBy;
      },
      set(value) {
        this.$emit("update:sortBy", value);
      },
    },
    toSortDesc: {
      get() {
        return this.sortDesc;
      },
      set(value) {
        this.$emit("update:sortDesc", value);
      },
    },
    toFilter: {
      get() {
        return this.filter;
      },
      set(value) {
        this.$emit("update:filter", value);
      },
    },
    sortOptions() {
      // Create an options list from our fields
      return this.fields
          .filter((f) => f.sortable)
          .map((f) => ({text: f.label, value: f.key}));
    },
  },
  methods: {
    opportunityCode({opportunity}) {
      return opportunity.is_won ? opportunity.project_code : opportunity.offer_code;
    },
    amountToCurrency,
    formatDate,
  }
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-sweetalert.scss";
</style>